import React, { useEffect, useRef, useState } from 'react';
import { Download } from 'lucide-react';

// Mappa di un processo (16/10/2026, prima: iscrizioni FIPAV). Fasi una sotto l'altra, in ogni fase i
// passi affiancati, frecce tra le fasi. È un'immagine SVG disegnata dai dati: si adatta alla larghezza
// (telefono = un passo per riga) e si scarica come PNG. Riutilizzabile per altri processi:
//   dati = { titolo, sottotitolo?, fasi: [{ titolo, colore, passi: [{ titolo, testo }] }], nota? }
export const FONT = "Figtree, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";
export const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

// a capo per larghezza (stima: carattere medio = 0,55 × dimensione)
export const aCapo = (testo, larghezza, dim) => {
  const max = Math.max(8, Math.floor(larghezza / (dim * 0.55)));
  const righe = [];
  for (const par of String(testo ?? '').split('\n')) {
    let riga = '';
    for (const parola of par.split(/\s+/).filter(Boolean)) {
      if (!riga) riga = parola;
      else if ((riga + ' ' + parola).length <= max) riga += ' ' + parola;
      else { righe.push(riga); riga = parola; }
    }
    righe.push(riga);
  }
  return righe;
};

export function disegnaMappa(dati, larghezza) {
  const W = Math.max(300, Math.round(larghezza));
  const P = W < 520 ? 14 : 24, G = 12, stretta = W < 620;
  const parti = [];
  let y = P;
  const linee = (righe, x, y0, dim, peso, colore, passo) =>
    righe.map((r, i) => `<text x="${x}" y="${y0 + i * passo}" font-size="${dim}" font-weight="${peso}" fill="${colore}" font-family="${FONT}">${esc(r)}</text>`).join('');

  const tit = aCapo(dati.titolo, W - 2 * P, 22);
  parti.push(linee(tit, P, y + 20, 22, 800, '#1D1D1F', 27));
  y += tit.length * 27 + 4;
  if (dati.sottotitolo) {
    const sub = aCapo(dati.sottotitolo, W - 2 * P, 13.5);
    parti.push(linee(sub, P, y + 16, 13.5, 400, '#6E6E73', 19));
    y += sub.length * 19 + 6;
  }
  y += 10;

  dati.fasi.forEach((f, fi) => {
    const col = f.colore || '#F4600C';
    const etichetta = `${fi + 1} · ${String(f.titolo).toUpperCase()}`;
    const lw = Math.min(W - 2 * P, etichetta.length * 7.6 + 26);
    parti.push(`<rect x="${P}" y="${y}" width="${lw}" height="26" rx="13" fill="${col}"/>`);
    parti.push(`<text x="${P + 13}" y="${y + 17.5}" font-size="12.5" font-weight="800" letter-spacing="0.6" fill="#fff" font-family="${FONT}">${esc(etichetta)}</text>`);
    y += 34;
    const perRiga = stretta ? 1 : Math.max(1, Math.min(f.passi.length, 3));
    for (let i = 0; i < f.passi.length; i += perRiga) {
      const gruppo = f.passi.slice(i, i + perRiga);
      const bw = (W - 2 * P - (perRiga - 1) * G) / perRiga;
      const misure = gruppo.map(p => {
        const t = aCapo(p.titolo, bw - 30, 14.5), d = aCapo(p.testo, bw - 30, 13);
        return { t, d, h: 14 + t.length * 19 + (p.testo ? 6 + d.length * 17.5 : 0) + 12 };
      });
      const h = Math.max(...misure.map(m => m.h));
      gruppo.forEach((p, j) => {
        const x = P + j * (bw + G), m = misure[j];
        parti.push(`<rect x="${x}" y="${y}" width="${bw}" height="${h}" rx="12" fill="#FFFFFF" stroke="${col}" stroke-opacity="0.35"/>`);
        parti.push(`<rect x="${x}" y="${y}" width="5" height="${h}" rx="2.5" fill="${col}"/>`);
        parti.push(linee(m.t, x + 16, y + 26, 14.5, 700, '#1D1D1F', 19));
        if (p.testo) parti.push(linee(m.d, x + 16, y + 26 + m.t.length * 19 + 4, 13, 400, '#3A3A3C', 17.5));
      });
      y += h + G;
    }
    if (fi < dati.fasi.length - 1) {
      const cx = W / 2;
      parti.push(`<line x1="${cx}" y1="${y - 4}" x2="${cx}" y2="${y + 14}" stroke="#A1A1A6" stroke-width="2"/>`);
      parti.push(`<path d="M ${cx - 6} ${y + 12} L ${cx + 6} ${y + 12} L ${cx} ${y + 20} Z" fill="#A1A1A6"/>`);
      y += 26;
    }
  });

  if (dati.nota) {
    y += 8;
    const n = aCapo(dati.nota, W - 2 * P - 28, 13);
    const h = 18 + n.length * 17.5;
    parti.push(`<rect x="${P}" y="${y}" width="${W - 2 * P}" height="${h}" rx="12" fill="#EFF6FF" stroke="#93C5FD" stroke-dasharray="5 4"/>`);
    parti.push(linee(n, P + 14, y + 23, 13, 500, '#1E3A8A', 17.5));
    y += h;
  }
  const H = Math.round(y + P);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><rect width="${W}" height="${H}" rx="18" fill="#F5F5F7"/>${parti.join('')}</svg>`;
  return { svg, w: W, h: H };
}

// PNG ad alta risoluzione (larghezza fissa, così è uguale da telefono e da computer)
export const scaricaPng = (dati, nomeFile, larghezza = 1100) => svgInPng(disegnaMappa(dati, larghezza), nomeFile);
// da un disegno { svg, w, h } a un file PNG scaricato (doppia risoluzione)
export async function svgInPng({ svg, w, h }, nomeFile) {
  const url = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }));
  try {
    const img = await new Promise((ok, ko) => { const i = new Image(); i.onload = () => ok(i); i.onerror = ko; i.src = url; });
    const canvas = document.createElement('canvas');
    canvas.width = w * 2; canvas.height = h * 2;
    const ctx = canvas.getContext('2d');
    ctx.scale(2, 2);
    ctx.drawImage(img, 0, 0);
    const blob = await new Promise(ok => canvas.toBlob(ok, 'image/png'));
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = nomeFile;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  } finally { URL.revokeObjectURL(url); }
}

export default function MappaProcesso({ dati, nomeFile = 'mappa.png' }) {
  const box = useRef(null);
  const [larghezza, setLarghezza] = useState(0);
  useEffect(() => {
    if (!box.current) return;
    const ro = new ResizeObserver(([e]) => setLarghezza(Math.floor(e.contentRect.width)));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  const { svg } = larghezza ? disegnaMappa(dati, Math.min(larghezza, 1100)) : { svg: '' };
  return (
    <div>
      <div ref={box} style={{ width: '100%' }} dangerouslySetInnerHTML={{ __html: svg }} />
      <button onClick={() => scaricaPng(dati, nomeFile)} style={{ marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '6px', border: '1px solid var(--border2)', background: 'var(--bg-card)', color: 'var(--text)', borderRadius: '999px', padding: '8px 14px', fontWeight: 600, cursor: 'pointer', fontSize: '13px' }}>
        <Download size={14} /> Scarica come immagine (PNG)
      </button>
    </div>
  );
}
